import React, {useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from "react";
import {LexoRank} from "lexorank";

import Logo from "../../components/UI-components/Logo";
import Button from "../../components/UI-components/Button";
import Loader from "../../components/UI-components/Loader";
import {Switch} from "../../components/UI-components/Switch";
import Popup from "../../components/UI-components/Popup";
import ReactDragListView from "react-drag-listview/lib/index.js";
import {connect} from "react-redux";
import * as actions from "../../store/actions";
import {PaginatedSelect} from "../../components/UI-components/PaginatedSelect";
import Tooltip from "../../components/UI-components/Tooltip";
import {isAdmin} from "../../services/Api";

const PAGE_SIZE = 20;
const GLOBAL_PREFETCH_PAGE_SIZE = PAGE_SIZE * 10;

const ModuleRanking = (props) => {
    const {
        isLoading,
        isSaving,
        isLocking,
        classes,
        modules: reduxModules,
        totalCount,
        lockInfo,
        saveResponse,
        currentUser,
        grants,
        getModuleRankingClasses,
        getModuleRanking,
        getModuleRankingPage,
        saveModuleRankingOrder,
        lockModuleRanking,
        unlockModuleRanking,
        getModuleRankingMonths,
        getModuleRankingLock,
        clearSaveModuleRankingResponse,
    } = props;

    const [selectedClass, setSelectedClass] = useState("");
    const [allModules, setAllModules] = useState([]);
    const [globalTotalCount, setGlobalTotalCount] = useState(0);
    const [filteredResults, setFilteredResults] = useState([]);
    const [filteredTotalCount, setFilteredTotalCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageInput, setPageInput] = useState("1");
    const [selectedRows, setSelectedRows] = useState(new Set());
    const [moveToPosition, setMoveToPosition] = useState("");
    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm);
    const [showNewModules, setShowNewModules] = useState(false);
    const [rankingChanges, setRankingChanges] = useState({});
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [showExitConfirm, setShowExitConfirm] = useState(false);
    const [showPositionNotLoadedPopup, setShowPositionNotLoadedPopup] = useState(false);
    const [recentlyMoved, setRecentlyMoved] = useState(new Set());
    const [showMonthsPopup, setShowMonthsPopup] = useState(false);
    const [rankingMonths, setRankingMonths] = useState([]);
    const [isLoadingMonths, setIsLoadingMonths] = useState(false);
    const [selectedMonth, setSelectedMonth] = useState("");
    const [showClusterWarningPopup, setShowClusterWarningPopup] = useState(false);
    const [clustersNotSubmitted, setClustersNotSubmitted] = useState([]);

    const rowRefs = useRef({});
    const prevPositions = useRef(null);
    const movedTimerRef = useRef(null);
    const loadingPageRef = useRef(null);
    const loadedGlobalPagesRef = useRef(new Set());
    const allowNextMergeOverwriteRef = useRef(false);

    const isFilteredView = showNewModules || debouncedSearchTerm.trim() !== "";

    const capturePositions = useCallback(() => {
        const positions = {};
        Object.entries(rowRefs.current).forEach(([name, el]) => {
            if (el) positions[name] = el.getBoundingClientRect().top;
        });
        prevPositions.current = positions;
    }, []);

    const allModuleRows = useMemo(() => {
        return (allModules || []).map((module, globalIndex) => ({module, globalIndex}));
    }, [allModules]);

    const filteredRows = useMemo(() => {
        return (filteredResults || []).map((module, filteredIndex) => {
            if (!module) return {module: null, globalIndex: null, filteredIndex};
            const currentGlobalIndex = allModules.findIndex(m => m?.moduleName === module.moduleName);
            const globalIndex = currentGlobalIndex >= 0
                ? currentGlobalIndex
                : (module.computedPosition ? module.computedPosition - 1 : -1);
            const globalModule = globalIndex >= 0 ? (allModules[globalIndex] || module) : module;
            return {module: globalModule, globalIndex, filteredIndex};
        });
    }, [allModules, filteredResults]);

    // Derived state for the currently displayed page
    const displayedRows = useMemo(() => {
        const sourceRows = isFilteredView ? filteredRows : allModuleRows;
        if (!sourceRows || sourceRows.length === 0) return [];
        const startIdx = (currentPage - 1) * PAGE_SIZE;
        const endIdx = Math.min(startIdx + PAGE_SIZE, sourceRows.length);
        const pageRows = sourceRows.slice(startIdx, endIdx);
        if (!isFilteredView) return pageRows;

        return [...pageRows].sort((rowA, rowB) => {
            if (!rowA.module) return 1;
            if (!rowB.module) return -1;
            return rowA.globalIndex - rowB.globalIndex;
        });
    }, [allModuleRows, currentPage, filteredRows, isFilteredView]);

    useLayoutEffect(() => {
        if (!prevPositions.current) return;
        const captured = prevPositions.current;
        prevPositions.current = null;

        requestAnimationFrame(() => {
            const animated = [];
            Object.entries(rowRefs.current).forEach(([name, el]) => {
                if (!el || captured[name] === undefined) return;
                const delta = captured[name] - el.getBoundingClientRect().top;
                if (Math.abs(delta) < 1) return;
                el.style.transition = 'none';
                el.style.transform = `translateY(${delta}px)`;
                animated.push(el);
            });
            if (animated.length === 0) return;
            requestAnimationFrame(() => {
                animated.forEach(el => {
                    el.style.transition = 'transform 0.35s ease';
                    el.style.transform = '';
                });
                animated[0].addEventListener('transitionend', () => {
                    animated.forEach(el => {
                        el.style.transition = '';
                        el.style.transform = '';
                    });
                }, {once: true});
            });
        });
    }, [displayedRows]);

    const flashMoved = useCallback((names) => {
        clearTimeout(movedTimerRef.current);
        setRecentlyMoved(new Set(names));
        movedTimerRef.current = setTimeout(() => setRecentlyMoved(new Set()), 650);
    }, []);

    const mergeModulesIntoGlobalList = useCallback((modulesToMerge) => {
        if (!modulesToMerge || modulesToMerge.length === 0) return;

        setAllModules(prev => {
            const nextModules = [...prev];
            modulesToMerge.forEach(module => {
                if (!module?.computedPosition) return;

                const globalIdx = module.computedPosition - 1;
                if (globalIdx < 0) return;

                if (globalIdx >= nextModules.length) {
                    const previousLength = nextModules.length;
                    nextModules.length = globalIdx + 1;
                    for (let i = previousLength; i < nextModules.length; i++) {
                        nextModules[i] = null;
                    }
                }

                const existingModule = nextModules[globalIdx];
                if (existingModule && hasUnsavedChanges && !allowNextMergeOverwriteRef.current) {
                    return;
                }

                nextModules[globalIdx] = module;
            });

            return nextModules;
        });
        allowNextMergeOverwriteRef.current = false;
    }, [hasUnsavedChanges]);

    const markUiPagesCoveredByPrefetch = useCallback((prefetchPage, modulesCount) => {
        if (!modulesCount) return;

        const firstPosition = prefetchPage * GLOBAL_PREFETCH_PAGE_SIZE + 1;
        const lastPosition = firstPosition + modulesCount - 1;
        const firstUiPage = Math.floor((firstPosition - 1) / PAGE_SIZE) + 1;
        const lastUiPage = Math.floor((lastPosition - 1) / PAGE_SIZE) + 1;

        for (let page = firstUiPage; page <= lastUiPage; page++) {
            loadedGlobalPagesRef.current.add(page);
        }
    }, []);

    const markUiPagesCoveredByPrefetchRequest = useCallback((prefetchPage) => {
        const firstPosition = prefetchPage * GLOBAL_PREFETCH_PAGE_SIZE + 1;
        const lastPosition = firstPosition + GLOBAL_PREFETCH_PAGE_SIZE - 1;
        const firstUiPage = Math.floor((firstPosition - 1) / PAGE_SIZE) + 1;
        const lastUiPage = Math.floor((lastPosition - 1) / PAGE_SIZE) + 1;

        for (let page = firstUiPage; page <= lastUiPage; page++) {
            loadedGlobalPagesRef.current.add(page);
        }
    }, []);

    const loadGlobalPagesForModules = useCallback((modulesToInspect) => {
        if (!modulesToInspect || modulesToInspect.length === 0) return;

        let maxGlobalPage = 0;
        modulesToInspect.forEach(module => {
            if (!module?.computedPosition) return;
            const page = Math.floor((module.computedPosition - 1) / PAGE_SIZE) + 1;
            maxGlobalPage = Math.max(maxGlobalPage, page);
        });

        if (maxGlobalPage === 0) return;

        const prefetchPagesToLoad = new Set();
        for (let page = 1; page <= maxGlobalPage; page++) {
            if (loadedGlobalPagesRef.current.has(page)) continue;
            const firstPositionInPage = (page - 1) * PAGE_SIZE + 1;
            const prefetchPage = Math.floor((firstPositionInPage - 1) / GLOBAL_PREFETCH_PAGE_SIZE);
            prefetchPagesToLoad.add(prefetchPage);
        }

        prefetchPagesToLoad.forEach(prefetchPage => {
            markUiPagesCoveredByPrefetchRequest(prefetchPage);
            getModuleRankingPage(selectedClass, prefetchPage, GLOBAL_PREFETCH_PAGE_SIZE, "", false)
                .then(({modules}) => {
                    markUiPagesCoveredByPrefetch(prefetchPage, modules.length);
                    mergeModulesIntoGlobalList(modules);
                })
                .catch((err) => {
                    console.error("Error loading global ranking page:", err);
                });
        });
    }, [getModuleRankingPage, markUiPagesCoveredByPrefetch, markUiPagesCoveredByPrefetchRequest, mergeModulesIntoGlobalList, selectedClass]);

    const fetchModules = useCallback((page, force = false) => {
        if (!force) {
            const startIdx = (page - 1) * PAGE_SIZE;
            const sourceList = isFilteredView ? filteredResults : allModules;
            if (sourceList.length > 0 && startIdx < sourceList.length) {
                const endIdx = Math.min(startIdx + PAGE_SIZE, sourceList.length);
                const slice = sourceList.slice(startIdx, endIdx);
                if (slice.every(m => m !== null)) {
                    return;
                }
            }
        }

        loadingPageRef.current = {page, isFiltered: isFilteredView};
        getModuleRanking(
            selectedClass,
            page - 1,
            PAGE_SIZE,
            debouncedSearchTerm,
            showNewModules
        );
    }, [
        allModules,
        debouncedSearchTerm,
        filteredResults,
        getModuleRanking,
        isFilteredView,
        selectedClass,
        showNewModules
    ]);

    const resetLoadedModulesComputedPositions = useCallback(() => {
        setAllModules(prevModules => prevModules.map((module, index) => {
            if (!module) return module;
            return {
                ...module,
                computedPosition: index + 1,
            };
        }));
    }, []);

    useEffect(() => {
        return () => clearTimeout(movedTimerRef.current);
    }, []);

    useEffect(() => {
        if (!props.isGAJustInizialized && props.ReactGA) {
            props.ReactGA.pageview(window.location.pathname + window.location.search);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        getModuleRankingClasses();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (classes && classes.length > 0 && !selectedClass) {
            setSelectedClass(classes[0].value);
        }
    }, [classes, selectedClass]);

    // Update allModules when new data arrives from Redux
    useEffect(() => {
        if (!reduxModules || isLoading) return;
        if (!loadingPageRef.current || loadingPageRef.current.page !== currentPage) return;

        const requestInfo = loadingPageRef.current;
        loadingPageRef.current = null;

        if (requestInfo.isFiltered) {
            setFilteredTotalCount(totalCount || 0);
            setFilteredResults(prev => {
                const nextResults = [...prev];
                if (totalCount > 0 && nextResults.length !== totalCount) {
                    nextResults.length = totalCount;
                    for (let i = 0; i < nextResults.length; i++) {
                        if (nextResults[i] === undefined) nextResults[i] = null;
                    }
                }

                const startIdx = (requestInfo.page - 1) * PAGE_SIZE;
                reduxModules.forEach((m, i) => {
                    nextResults[startIdx + i] = m;
                });

                return nextResults;
            });

            mergeModulesIntoGlobalList(reduxModules);
            loadGlobalPagesForModules(reduxModules);
            return;
        }

        setAllModules(prev => {
            let newAll = [...prev];
            if (totalCount > 0 && newAll.length !== totalCount) {
                // Resize only if empty or no changes at all
                if (newAll.length === 0 || (!isEditMode && !hasUnsavedChanges)) {
                    newAll = new Array(totalCount).fill(null);
                }
            }

            const startIdx = (requestInfo.page - 1) * PAGE_SIZE;
            reduxModules.forEach((m, i) => {
                const globalIdx = startIdx + i;
                if (globalIdx < newAll.length) {
                    // Safety sync: only update if we haven't touched this slot locally
                    // OR if we don't have unsaved changes (safe fresh sync)
                    if (newAll[globalIdx] === null || !hasUnsavedChanges) {
                        newAll[globalIdx] = m;
                    }
                }
            });
            return newAll;
        });
        loadedGlobalPagesRef.current.add(requestInfo.page);
        setGlobalTotalCount(totalCount || 0);
    }, [reduxModules, currentPage, totalCount, isEditMode, isLoading, hasUnsavedChanges, loadGlobalPagesForModules, mergeModulesIntoGlobalList]);

    useEffect(() => {
        if (selectedClass) {
            setCurrentPage(1);
            setPageInput("1");
            setSelectedRows(new Set());
            setRankingChanges({});
            setHasUnsavedChanges(false);
            setAllModules([]);
            setFilteredResults([]);
            setFilteredTotalCount(0);
            setGlobalTotalCount(0);
            loadedGlobalPagesRef.current = new Set();
            fetchModules(1, true);
            getModuleRankingLock(selectedClass);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedClass]);

    useEffect(() => {
        if (selectedClass) {
            setCurrentPage(1);
            setPageInput("1");
            setSelectedRows(new Set());
            setFilteredResults([]);
            setFilteredTotalCount(0);
            fetchModules(1, true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showNewModules]);

    // Re-fetch when search changes
    useEffect(() => {
        if (selectedClass) {
            setCurrentPage(1);
            setPageInput("1");
            setSelectedRows(new Set());
            setFilteredResults([]);
            setFilteredTotalCount(0);
            fetchModules(1, true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedSearchTerm]);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, 500);

        return () => {
            clearTimeout(handler);
        };
    }, [searchTerm]);

    useEffect(() => {
        if (selectedClass) {
            fetchModules(currentPage);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage]);

    // Unlock the class if the user closes the page while editing
    useEffect(() => {
        return () => {
            if (isEditMode) {
                unlockModuleRanking(selectedClass);
            }
        };
    }, [isEditMode, selectedClass, unlockModuleRanking]);

    useEffect(() => {
        if (!saveResponse) return;
        
        setHasUnsavedChanges(false);
        setRankingChanges({});
        resetLoadedModulesComputedPositions();
        setSelectedRows(new Set());
        setShowMonthsPopup(false);
        setFilteredResults([]);
        setFilteredTotalCount(0);
        allowNextMergeOverwriteRef.current = true;
        fetchModules(currentPage, true);

        if (saveResponse?.totalClusterNotSubmitted > 0) {
            setClustersNotSubmitted(saveResponse?.clusterNotSubmitted);
            setShowClusterWarningPopup(true);
        } else {
            setClustersNotSubmitted([]);
            setShowClusterWarningPopup(false);
        }

        clearSaveModuleRankingResponse();
    }, [saveResponse, clearSaveModuleRankingResponse, currentPage, fetchModules, resetLoadedModulesComputedPositions]);

    const handleSaveOrder = () => {
        setIsLoadingMonths(true);
        getModuleRankingMonths(selectedClass)
            .then((months) => {
                const now = new Date();
                const mm = String(now.getMonth() + 1).padStart(2, '0');
                const currentMonthId = `M${mm}_${now.getFullYear()}`;
                const defaultMonth = months.includes(currentMonthId) ? currentMonthId : (months[0] || "");
                setRankingMonths(months);
                setSelectedMonth(defaultMonth);
                setShowMonthsPopup(true);
            })
            .finally(() => setIsLoadingMonths(false));
    };

    const handleConfirmSave = () => {
        saveModuleRankingOrder(
            selectedClass,
            rankingChanges,
            currentPage - 1,
            PAGE_SIZE,
            debouncedSearchTerm,
            showNewModules,
            selectedMonth
        );
    };

    /**
     * Helper to update LexoRanks for a set of items being moved to a new target index.
     * Moved items are physically reordered; then unranked loaded items in the affected
     * range receive a rank too, so an unranked prefix is persisted consistently.
     */
    const updateRanksForMovedItems = (list, movedIndices, targetIndex, currentChanges) => {
        const newList = [...list];
        const itemsToMove = [];
        const movedNames = new Set();
        
        [...movedIndices].sort((a, b) => b - a).forEach(idx => {
            const [item] = newList.splice(idx, 1);
            if (item) {
                itemsToMove.unshift(item);
                movedNames.add(item.moduleName);
            }
        });

        const insertIndex = Math.min(Math.max(targetIndex, 0), newList.length);
        newList.splice(insertIndex, 0, ...itemsToMove);

        const newChanges = { ...currentChanges };
        const maxAffectedIndex = Math.max(
            ...movedIndices,
            insertIndex + itemsToMove.length - 1
        );

        for (let i = 0; i <= maxAffectedIndex && i < newList.length; i++) {
            const currentItem = newList[i];
            if (!currentItem) continue;

            const isMovedItem = movedNames.has(currentItem.moduleName);
            const needsRank = !currentItem.rankingPosition || isMovedItem;
            if (!needsRank) continue;

            let prevRank = LexoRank.min();
            for (let k = i - 1; k >= 0; k--) {
                if (newList[k]?.rankingPosition) {
                    prevRank = LexoRank.parse(newList[k].rankingPosition);
                    break;
                }
            }

            let nextRank = LexoRank.max();
            for (let k = i + 1; k < newList.length; k++) {
                if (newList[k]?.rankingPosition && !movedNames.has(newList[k].moduleName)) {
                    const parsed = LexoRank.parse(newList[k].rankingPosition);
                    if (parsed.toString() > prevRank.toString()) {
                        nextRank = parsed;
                        break;
                    }
                }
            }

            const newRank = prevRank.between(nextRank).toString();
            newList[i] = { ...currentItem, rankingPosition: newRank };
            newChanges[currentItem.moduleName] = newRank;
        }

        return { updatedList: newList, newChanges };
    };

    const handleDragEnd = (fromIndex, toIndex) => {
        if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0) return;

        const pageStartIndex = (currentPage - 1) * PAGE_SIZE;
        const globalFrom = displayedRows[fromIndex]?.globalIndex ?? (pageStartIndex + fromIndex);
        const globalTo = displayedRows[toIndex]?.globalIndex ?? (pageStartIndex + toIndex);

        const draggedItem = allModules[globalFrom];
        if (!draggedItem) return;

        const { updatedList, newChanges } = updateRanksForMovedItems(allModules, [globalFrom], globalTo, rankingChanges);

        capturePositions();
        setRankingChanges(newChanges);
        setAllModules(updatedList);
        setHasUnsavedChanges(true);
        flashMoved([draggedItem.moduleLabel]);
    };

    const handleSelectAll = (e) => {
        if (e.target.checked) {
            const allIds = new Set(displayedRows.filter(row => row.module).map((row) => row.module.moduleLabel));
            setSelectedRows(allIds);
        } else {
            setSelectedRows(new Set());
        }
    };

    const handleSelectRow = (moduleLabel) => {
        const newSelected = new Set(selectedRows);
        if (newSelected.has(moduleLabel)) {
            newSelected.delete(moduleLabel);
        } else {
            newSelected.add(moduleLabel);
        }
        setSelectedRows(newSelected);
    };

    const handleMoveSelected = () => {
        const targetPos = parseInt(moveToPosition, 10);
        if (isNaN(targetPos) || targetPos < 1 || selectedRows.size === 0) return;
        const maxGlobalPosition = globalTotalCount || allModules.length || totalCount;
        if (targetPos > maxGlobalPosition) return;

        const globalTargetIndex = targetPos - 1;
        const selectedIndices = [];
        
        allModules.forEach((m, idx) => {
            if (m && selectedRows.has(m.moduleLabel)) {
                selectedIndices.push(idx);
            }
        });

        if (selectedIndices.length === 0) return;

        // Range check: everything between indices and target must be loaded to calculate ranks accurately
        const minRangeIdx = Math.min(...selectedIndices, globalTargetIndex);
        const maxRangeIdx = Math.max(...selectedIndices, globalTargetIndex);
        for (let i = minRangeIdx; i <= maxRangeIdx; i++) {
            if (allModules[i] === null) {
                setShowPositionNotLoadedPopup(true);
                return;
            }
        }

        const { updatedList, newChanges } = updateRanksForMovedItems(allModules, selectedIndices, globalTargetIndex, rankingChanges);

        capturePositions();
        setAllModules(updatedList);
        setRankingChanges(newChanges);
        setHasUnsavedChanges(true);
        setMoveToPosition("");
        setSelectedRows(new Set());
        flashMoved([...selectedRows]);
    };

    const handlePageChange = (pageNumber) => {
        const validPage = Math.max(1, Math.min(pageNumber, totalPages));
        setCurrentPage(validPage);
        setPageInput(validPage.toString());
    };

    const handlePageInputChange = (e) => {
        setPageInput(e.target.value);
    };

    const handlePageInputBlur = () => {
        let targetPage = parseInt(pageInput, 10);
        if (isNaN(targetPage) || targetPage < 1) {
            targetPage = 1;
        } else if (targetPage > totalPages) {
            targetPage = totalPages;
        }
        if (targetPage !== currentPage) {
            handlePageChange(targetPage);
        } else {
            setPageInput(currentPage.toString());
        }
    };

    const handlePageInputKeyDown = (e) => {
        if (e.key === "Enter") {
            handlePageInputBlur();
            e.target.blur();
        }
    };

    const exitEditMode = ({ clearModules = false } = {}) => {
        setIsEditMode(false);
        setSelectedRows(new Set());
        setMoveToPosition("");
        setRankingChanges({});
        setHasUnsavedChanges(false);
        if (clearModules) {
            setAllModules([]);
        }
    };

    const getCurrentPosition = (module, index, globalIndex = null) => {
        if (globalIndex !== null && globalIndex !== undefined && globalIndex >= 0) {
            return globalIndex + 1;
        }
        return (currentPage - 1) * PAGE_SIZE + index + 1;
    };

    const getPositionDisplay = (module, index, globalIndex) => {
        const currentPos = getCurrentPosition(module, index, globalIndex);
        const originalPos = module.computedPosition || currentPos;
        const diff = originalPos - currentPos;

        if (diff === 0) return `${currentPos} ${module.rankingPosition ? '' : '(*)'}`;
        if (diff > 0) return `${currentPos} (+${diff})`;
        return `${currentPos} (${diff})`;
    };

    const isRowChanged = (module, index, globalIndex) => {
        if (!module) return false;
        const currentPos = getCurrentPosition(module, index, globalIndex);
        const originalPos = module.computedPosition || currentPos;
        return originalPos !== currentPos || !!rankingChanges[module.moduleName];
    };

    const isRowChangedNegative = (module, index, globalIndex) => {
        if (!module) return false;
        const currentPos = getCurrentPosition(module, index, globalIndex);
        const originalPos = module.computedPosition || currentPos;
        return (originalPos - currentPos) < 0;
    };

    const activeTotalCount = isFilteredView ? filteredTotalCount : (globalTotalCount || totalCount);
    const totalPages = Math.max(1, Math.ceil(activeTotalCount / PAGE_SIZE));
    const unsavedCount = Object.keys(rankingChanges).length;
    const canEditRanking = isAdmin(grants);

    const dragProps = isEditMode
        ? {
            onDragEnd: handleDragEnd,
            nodeSelector: ".module-ranking-row",
            handleSelector: ".module-ranking-drag-handle",
        }
        : {
            onDragEnd: () => {
            },
            nodeSelector: ".module-ranking-row",
            handleSelector: ".module-ranking-drag-handle-disabled",
        };

    return (
        <div className="module-ranking-page">
            <header className="minimal-header">
                <Logo/>
                <h2>RANKING MODULE</h2>
            </header>

            <div className="module-ranking-container">
                <div className="module-ranking-toolbar">
                    <div className={`toolbar-row${isEditMode ? " toolbar-row--with-border" : ""}`}>
                        <div className="toolbar-item toolbar-class-select">
                            <PaginatedSelect
                                placeholder="Class"
                                options={classes}
                                value={classes.find((cls) => (cls.value || cls) === selectedClass) || null}
                                disabled={isEditMode}
                                handleChange={(selectedOption) => {
                                    if (isEditMode) return;
                                    setSelectedClass(selectedOption ? selectedOption.value : "");
                                }}
                            />
                        </div>

                        <div className="toolbar-separator"/>

                        <div className="toolbar-item toolbar-search">
                            <input
                                type="text"
                                placeholder="Search by label,name or description"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="toolbar-input toolbar-input--search"
                            />
                        </div>

                        <div className="toolbar-separator"/>

                        <div className="toolbar-item toolbar-switch">
                            <Switch
                                isChecked={showNewModules}
                                onChange={() => setShowNewModules(!showNewModules)}
                                label={["Show new Modules", "Show new Modules"]}
                            />
                        </div>

                        <div className="toolbar-separator"/>

                        <div className="toolbar-actions">
                            {isEditMode ? (
                                <Button
                                    btnClass="btn-medium"
                                    disabled={isLocking}
                                    handleClick={() => {
                                        if (hasUnsavedChanges) {
                                            setShowExitConfirm(true);
                                        } else {
                                            exitEditMode();
                                        }
                                    }}
                                >
                                    &#10005; DONE EDITING
                                </Button>
                            ) : selectedClass && canEditRanking && (() => {
                                const isLockedByOther = !!lockInfo?.userLock &&
                                    lockInfo.userLock.toLowerCase() !== (currentUser?.user_name || "").toLowerCase();
                                const editBtn = (
                                    <Button
                                        btnClass="btn-medium"
                                        disabled={isLocking || isLockedByOther}
                                        handleClick={() => {
                                            lockModuleRanking(selectedClass)
                                                .then(() => setIsEditMode(true))
                                                .catch(() => alert("Unable to acquire lock for this class."));
                                        }}
                                    >
                                        EDIT
                                    </Button>
                                );
                                return isLockedByOther ? (
                                    <Tooltip
                                        tooltipClass="tooltip-lock left"
                                        tooltipTextClass="white"
                                        text={`This Ranking is locked by ${lockInfo.userLock}`}
                                    >
                                        {editBtn}
                                    </Tooltip>
                                ) : editBtn;
                            })()}
                        </div>
                    </div>

                    {isEditMode && <div className="toolbar-row">
                        <button
                            className="toolbar-reset-btn"
                            onClick={() => {
                                setSearchTerm("");
                                setDebouncedSearchTerm("");
                                setMoveToPosition("");
                                setShowNewModules(false);
                                setSelectedRows(new Set());
                                setRankingChanges({});
                                setHasUnsavedChanges(false);
                                setAllModules([]);
                                if (currentPage === 1 && debouncedSearchTerm === "" && !showNewModules) {
                                    fetchModules(1, true);
                                } else {
                                    setCurrentPage(1);
                                    setPageInput("1");
                                }
                            }}
                            title="Reset filters and changes"
                        >
                            <span className="toolbar-reset-icon">&#8634;</span>
                            Reset
                        </button>

                        <div className="toolbar-separator"/>

                        <div className="toolbar-item toolbar-move">
                            <label>Move to pos:</label>
                            <div className="toolbar-move-group">
                                <input
                                    type="number"
                                    min={1}
                                    value={moveToPosition}
                                    onChange={(e) => setMoveToPosition(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") {
                                            e.preventDefault();
                                            handleMoveSelected();
                                        }
                                    }}
                                    className="toolbar-move-input"
                                    placeholder="#"
                                />
                                <button
                                    className="toolbar-move-btn"
                                    onClick={handleMoveSelected}
                                    disabled={!moveToPosition || selectedRows.size === 0}
                                    title="Move"
                                >
                                    &#8594;
                                </button>
                            </div>
                            <span className="toolbar-move-hint">
                {selectedRows.size > 0
                    ? `${selectedRows.size} selected`
                    : "No selection"}
              </span>
                        </div>

                        <div className="toolbar-actions">
                            <Button
                                btnClass="btn-medium"
                                handleClick={handleSaveOrder}
                                disabled={!hasUnsavedChanges || isSaving || isLoadingMonths}
                            >
                                {isSaving ? "Saving..." : isLoadingMonths ? "..." : `SAVE NEW ORDER ${unsavedCount > 0 ? `(${unsavedCount})` : ""}`}
                            </Button>
                        </div>
                    </div>}
                </div>

                <div className="module-ranking-results">
                    {isLoading && displayedRows.length === 0 ? (
                        <Loader/>
                    ) : displayedRows.length === 0 ? (
                        <div className="empty-state-container">
                            <Logo/>
                            <p className="empty-state-message">No modules found.</p>
                        </div>
                    ) : (
                        <>
                            <div className="table-container">
                                <table className="module-ranking-table header-container">
                                    <thead>
                                    <tr>
                                        {isEditMode && <th className="col-drag"></th>}
                                        {isEditMode && <th className="col-checkbox">
                                            <input
                                                type="checkbox"
                                                onChange={handleSelectAll}
                                                checked={
                                                    displayedRows.length > 0 &&
                                                    displayedRows.every(row => row.module && selectedRows.has(row.module.moduleLabel))
                                                }
                                            />
                                        </th>}
                                        <th className="col-pos">Pos</th>
                                        <th className="col-label">Module Label</th>
                                        <th className="col-name">Module Name</th>
                                        <th className="col-desc">Module Description</th>
                                    </tr>
                                    </thead>
                                </table>

                                <ReactDragListView {...dragProps}>
                                    <table className="module-ranking-table">
                                        <tbody>
                                        {displayedRows.map((row, index) => {
                                            const module = row.module;
                                            const globalIndex = row.globalIndex;
                                            if (!module) return (
                                                <tr key={`loading-${index}`} className="module-ranking-row row-loading">
                                                    <td colSpan="100%" style={{textAlign: 'center', padding: '10px'}}>Loading...</td>
                                                </tr>
                                            );
                                            const moduleLabel = module.moduleLabel;
                                            const isSelected = selectedRows.has(moduleLabel);
                                            const changed = isRowChanged(module, index, globalIndex);

                                            return (
                                                <tr
                                                    key={moduleLabel}
                                                    ref={(el) => {
                                                        if (el) rowRefs.current[moduleLabel] = el;
                                                        else delete rowRefs.current[moduleLabel];
                                                    }}
                                                    className={`module-ranking-row${changed ? " row-changed" : ""}${isSelected ? " row-selected" : ""}${index % 2 === 0 ? " row-even" : ""}${recentlyMoved.has(moduleLabel) ? " row-just-moved" : ""}`}
                                                >
                                                    {isEditMode && <td className="col-drag">
                                                        <span className="module-ranking-drag-handle">&#9776;</span>
                                                    </td>}
                                                    {isEditMode && <td className="col-checkbox">
                                                        <input
                                                            type="checkbox"
                                                            checked={isSelected}
                                                            onChange={() => handleSelectRow(moduleLabel)}
                                                        />
                                                    </td>}
                                                    <td className="col-pos">
                              <span
                                  className={changed ? (isRowChangedNegative(module, index, globalIndex) ? "pos-changed-negative" : "pos-changed") : ""}>
                                {getPositionDisplay(module, index, globalIndex)}
                              </span>
                                                    </td>
                                                    <td className="col-label">
                                                        <strong>{module.moduleLabel || ""}</strong>
                                                    </td>
                                                    <td className="col-name">
                                                        <strong>{module.moduleName || ""}</strong>
                                                    </td>
                                                    <td className="col-desc">
                                                        {module.moduleDescription || ""}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                        </tbody>
                                    </table>
                                </ReactDragListView>
                            </div>

                            <div className="pagination-controls">
                <span className="page-info page-input-container">
                  Go to page
                  <input
                      type="number"
                      value={pageInput}
                      onChange={handlePageInputChange}
                      onBlur={handlePageInputBlur}
                      onKeyDown={handlePageInputKeyDown}
                      min="1"
                      max={totalPages}
                      className="page-input"
                      disabled={isSaving}
                  />
                  <Button
                      btnClass="btn-small btn-pagination"
                      handleClick={handlePageInputBlur}
                      disabled={isSaving}
                  >
                    Apply
                  </Button>
                </span>
                                <div className="pagination-buttons-group">
                                    <Button
                                        btnClass="btn-small btn-pagination"
                                        handleClick={() => handlePageChange(currentPage - 1)}
                                        disabled={currentPage === 1 || isSaving}
                                    >
                                        &laquo; Previous
                                    </Button>
                                    <span className="page-info">
                    Page {currentPage} of {totalPages}
                  </span>
                                    <Button
                                        btnClass="btn-small btn-pagination"
                                        handleClick={() => handlePageChange(currentPage + 1)}
                                        disabled={currentPage === totalPages || isSaving}
                                    >
                                        Next &raquo;
                                    </Button>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>

            {showExitConfirm && (
                <Popup
                    title="Confirm exit"
                    popupClass="popup-medium popup-exit-confirm"
                    close={() => setShowExitConfirm(false)}
                    isDoubleButton
                    btnName="Confirm"
                    btnName2="Cancel"
                    handleClick={() => {
                        setShowExitConfirm(false);
                        exitEditMode({ clearModules: true });
                        fetchModules(currentPage);
                    }}
                >
                    <p>You have unsaved changes. Exiting edit mode will discard them. Do you want to continue?</p>
                </Popup>
            )}

            {showMonthsPopup && (
                <Popup
                    title="Confirm save"
                    popupClass="popup-medium popup-save-ranking"
                    close={() => setShowMonthsPopup(false)}
                    isDoubleButton
                    btnName="Confirm"
                    btnName2="Cancel"
                    handleClick={handleConfirmSave}
                    disabled={!selectedMonth}
                >
                    <div className="save-ranking-month-container">
                        <p>The new order will be applied starting from the selected month:</p>
                        <select
                            className="toolbar-input save-ranking-month-select"
                            value={selectedMonth}
                            onChange={(e) => setSelectedMonth(e.target.value)}
                        >
                            {rankingMonths.length === 0 ? (
                                <option value="">No months available</option>
                            ) : (
                                rankingMonths.map((month, idx) => {
                                    const monthId = typeof month === 'object' ? month.monthId : month;
                                    return (
                                        <option key={monthId || idx} value={monthId}>{monthId}</option>
                                    );
                                })
                            )}
                        </select>
                    </div>
                </Popup>
            )}

            {showClusterWarningPopup && (
                <Popup
                    title="Clusters not submitted"
                    popupClass="popup-medium popup-save-ranking"
                    close={() => {
                        setShowClusterWarningPopup(false);
                        setClustersNotSubmitted([]);
                    }}
                    btnName="Close"
                    handleClick={() => {
                        setShowClusterWarningPopup(false);
                        setClustersNotSubmitted([]);
                    }}
                >
                    <div className="save-ranking-month-container">
                        <p>
                            The ranking was saved, but some clusters are not submitted. <br/>
                            Please submit them manually:
                        </p>
                        <ul>
                            {clustersNotSubmitted.map((cluster, idx) => (
                                <li key={`${cluster.clusterId || "cluster"}-${cluster.monthId || idx}`}>
                                    {cluster.clusterId} - {cluster.monthId}
                                </li>
                            ))}
                        </ul>
                    </div>
                </Popup>
            )}

            {showPositionNotLoadedPopup && (
                <Popup
                    title="Position not loaded"
                    popupClass="popup-medium"
                    close={() => setShowPositionNotLoadedPopup(false)}
                    btnName="OK"
                    handleClick={() => setShowPositionNotLoadedPopup(false)}
                >
                    <p>
                        To move a module to this position, you must first load the pages in between by visiting them.
                        Please scroll through the pages to load the necessary data.
                    </p>
                </Popup>
            )}
        </div>
    );
};

const mapStateToProps = (state) => {
    return {
        isLoading: state.moduleRanking?.isLoading || false,
        isClassesLoading: state.moduleRanking?.isClassesLoading || false,
        isSaving: state.moduleRanking?.isSaving || false,
        isLocking: state.moduleRanking?.isLocking || false,
        classes: state.moduleRanking?.classes || [],
        classesTotalCount: state.moduleRanking?.classesTotalCount || 0,
        modules: state.moduleRanking?.modules || [],
        totalCount: state.moduleRanking?.totalCount || 0,
        lockInfo: state.moduleRanking?.lockInfo || null,
        saveResponse: state.moduleRanking?.saveResponse || null,
        currentUser: state.authentication?.user || null,
        grants: state.authentication?.grants || null,
    };
};

const mapDispatchToProps = (dispatch) => {
    return {
        logout: () => dispatch(actions.logout()),
        getModuleRankingClasses: (search, pageIndex, pageSize) => dispatch(actions.getModuleRankingClasses(search, pageIndex, pageSize)),
        getModuleRanking: (classId, pageIndex, pageSize, search, showNewModules) =>
            dispatch(actions.getModuleRanking(classId, pageIndex, pageSize, search, showNewModules)),
        getModuleRankingPage: (classId, pageIndex, pageSize, search, showNewModules) =>
            dispatch(actions.getModuleRankingPage(classId, pageIndex, pageSize, search, showNewModules)),
        saveModuleRankingOrder: (classId, rankingChanges, pageIndex, pageSize, search, showNewModules, fromMonth) =>
            dispatch(actions.saveModuleRankingOrder(classId, rankingChanges, pageIndex, pageSize, search, showNewModules, fromMonth)),
        clearSaveModuleRankingResponse: () => dispatch(actions.clearSaveModuleRankingResponse()),
        lockModuleRanking: (classId) => dispatch(actions.lockModuleRanking(classId)),
        unlockModuleRanking: (classId) => dispatch(actions.unlockModuleRanking(classId)),
        getModuleRankingMonths: (classId) => dispatch(actions.getModuleRankingMonths(classId)),
        getModuleRankingLock: (classId) => dispatch(actions.getModuleRankingLock(classId)),
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(ModuleRanking);
